// Спільне для вікна «дзвонить», списку дзвінків і вкладки в картці.
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../contexts/ToastContext';
import { apiFetch } from '../../services/api';
import { getChannelsStatus, invalidateChannelsStatus } from '../../services/channelsStatus';

// Телефонія з /api/channels-status: { configured, myLine }. Вибрав лінію в
// одному місці — оновлюються всі кнопки й вікно дзвінка.
const telListeners = new Set();
export function useTelephony() {
  const [tel, setTel] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => getChannelsStatus().then(s => { if (!cancelled) setTel(s?.telephony || null); }).catch(() => {});
    load();
    telListeners.add(load);
    return () => { cancelled = true; telListeners.delete(load); };
  }, []);
  return tel;
}

async function saveMyLine(line) {
  const r = await apiFetch('/api/telephony?action=my-line', { method: 'POST', body: { line } });
  invalidateChannelsStatus();
  telListeners.forEach(load => load());
  return r.line;
}

export const fmtDuration = (sec) => {
  const s = Number(sec) || 0;
  if (!s) return '';
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const fmtWhen = (ts) => (ts
  ? new Date(ts).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '');

/** Коротко, що сталося з дзвінком. */
export function callSummary(c) {
  if (c.state === 'ringing') return 'дзвонить';
  if (c.state === 'answered') return 'розмова';
  if (c.missed) return c.handledAt ? `пропущений · ${c.handledBy || 'розібрались'}` : 'пропущений';
  if (c.direction === 'out' && !c.answeredAt) return 'не додзвонились';
  return fmtDuration(c.billSec) || 'завершено';
}

export function CallIcon({ call }) {
  const cls = call.missed ? 'missed' : call.direction === 'out' ? 'out' : 'in';
  const ch = call.missed ? '✕' : call.direction === 'out' ? '↗' : '↙';
  return <span className={`call-icon ${cls}`} title={cls === 'missed' ? 'Пропущений' : cls === 'out' ? 'Вихідний' : 'Вхідний'}>{ch}</span>;
}

// ── Результат розмови (етап 3 ТЗ телефонії, 23.10) ──
// Необовʼязковий: «домовились» чи «не домовились» з причиною зі списку
// закладу. Ставиться лише там, де розмова була.

export const canRate = (c) => !!c.answeredAt && c.state === 'ended';

export function outcomeLabel(c) {
  if (c.outcome === 'agreed') return 'Домовились';
  if (c.outcome === 'declined') return c.outcomeReason ? `Не домовились: ${c.outcomeReason}` : 'Не домовились';
  return '';
}

// Причини — один запит на вкладку; скидається, коли їх зберігають.
let reasonsCache = null;
export function invalidateOutcomeReasons() { reasonsCache = null; }
function loadReasons() {
  if (!reasonsCache) {
    reasonsCache = apiFetch('/api/telephony?action=outcomes')
      .then(r => r.reasons || [])
      .catch(() => { reasonsCache = null; return []; });
  }
  return reasonsCache;
}
export function useOutcomeReasons() {
  const [reasons, setReasons] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadReasons().then(r => { if (!cancelled) setReasons(r); });
    return () => { cancelled = true; };
  }, []);
  return reasons;
}

/** Зберегти результат; повертає оновлений дзвінок. */
export async function saveOutcome(id, outcome, reason = null) {
  const r = await apiFetch('/api/telephony?action=outcome', { method: 'POST', body: { id, outcome, reason } });
  return r.call;
}

const encode = (c) => (c.outcome === 'agreed' ? 'agreed'
  : c.outcome === 'declined' ? (c.outcomeReason ? `declined::${c.outcomeReason}` : 'declined') : '');

/** Компактний вибір результату для списків. */
export function OutcomeSelect({ call, reasons, onChange, disabled }) {
  const value = encode(call);
  const list = call.outcomeReason && !reasons.includes(call.outcomeReason) ? [...reasons, call.outcomeReason] : reasons;
  const change = (e) => {
    const v = e.target.value;
    if (!v) return onChange(null, null);
    if (v === 'agreed') return onChange('agreed', null);
    return onChange('declined', v.startsWith('declined::') ? v.slice('declined::'.length) : null);
  };
  return (
    <select className={`call-outcome-select${call.outcome ? ` ${call.outcome}` : ''}`} value={value} onChange={change} disabled={disabled} aria-label="Результат розмови">
      <option value="">{call.outcome ? '— зняти результат —' : 'Результат…'}</option>
      <option value="agreed">✓ Домовились</option>
      <optgroup label="✕ Не домовились">
        {list.map(r => <option key={r} value={`declined::${r}`}>{r}</option>)}
        <option value="declined">Без причини</option>
      </optgroup>
    </select>
  );
}

// ── «Подзвонити» (30.10) ──
// Binotel спершу дзвонить на внутрішню лінію того, хто натиснув, а коли
// той візьме слухавку — клієнту з номера закладу (номер закладу обирає сам
// Binotel). 31.10: лінію ніхто не розставляє заздалегідь — при першому
// натисканні людина сама вибирає, з якого телефону дзвонить; вибір
// запамʼятовується за нею, а одна лінія може бути в кількох (рецепція).

/** «З якого телефону дзвоните?» — лінії з Binotel або номер вручну. */
function LinePicker({ current, onPick, onClose }) {
  const [lines, setLines] = useState(null);
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/telephony?action=lines')
      .then(r => { if (!cancelled) setLines(r.lines || []); })
      .catch(() => { if (!cancelled) setLines([]); });
    return () => { cancelled = true; };
  }, []);
  const pick = async (line) => {
    if (busy || !line) return;
    setBusy(true);
    try { await onPick(line); } finally { setBusy(false); }
  };
  return createPortal(
    <div className="modal-overlay dial-line-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-sm" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>З якого телефону дзвоните?</h2>
          <button type="button" className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body dial-line-body">
          <p className="api-keys-muted">
            Binotel спершу подзвонить на цей телефон, а коли візьмете слухавку — набере клієнта. Вибір запамʼятається, змінити можна будь-коли.
          </p>
          {lines === null ? (
            <p className="api-keys-muted">Завантаження…</p>
          ) : lines.length > 0 && (
            <div className="dial-line-list">
              {lines.map(l => (
                <button key={l.number} type="button" disabled={busy} onClick={() => pick(l.number)}
                  className={`dial-line-option${l.number === current ? ' selected' : ''}`}>
                  <strong>{l.number}</strong>{l.name ? <span>{l.name}</span> : null}
                </button>
              ))}
            </div>
          )}
          {/* Портал не рятує від React-спливання: у картці клієнта вкладка
              «Дзвінки» — всередині <form> картки, і без stopPropagation цей
              submit зберіг би картку. */}
          <form className="dial-line-manual" onSubmit={e => { e.preventDefault(); e.stopPropagation(); pick(manual); }}>
            <input type="text" inputMode="numeric" maxLength={10} value={manual} placeholder="Інший внутрішній номер"
              onChange={e => setManual(e.target.value.replace(/\D/g, ''))} />
            <button type="submit" className="btn btn-secondary btn-sm" disabled={busy || !manual}>Вибрати</button>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function DialButton({ phone }) {
  const toast = useToast();
  const tel = useTelephony();
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  if (!phone || !tel?.configured) return null;

  const dial = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/telephony?action=dial', { method: 'POST', body: { phone } });
      toast.success(`Binotel дзвонить на ваш телефон ${r.line}. Візьміть слухавку — і він набере клієнта.`);
    } catch (err) {
      if (err?.body?.error === 'no_line') setPicking(true);
      else toast.error(err?.message || 'Не вдалося почати дзвінок');
    }
    setBusy(false);
  };

  const onPick = async (line) => {
    try {
      await saveMyLine(line);
    } catch (err) {
      toast.error(err?.message || 'Не вдалося зберегти');
      return;
    }
    setPicking(false);
    await dial();
  };

  return (
    <>
      <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
        onClick={() => (tel.myLine ? dial() : setPicking(true))}
        title={tel.myLine ? `Подзвонити ${phone} з телефону ${tel.myLine}` : `Подзвонити ${phone}`}>
        📞 Подзвонити
      </button>
      {picking && <LinePicker current={tel.myLine} onPick={onPick} onClose={() => setPicking(false)} />}
    </>
  );
}

/**
 * Свій телефон для «Подзвонити»: «з 904 · змінити» біля кнопки в картці,
 * повний підпис — над списком «Дзвінків». Поки не вибрано, compact нічого
 * не показує: кнопка сама спитає при першому натисканні.
 */
export function MyLine({ compact = false }) {
  const toast = useToast();
  const tel = useTelephony();
  const [open, setOpen] = useState(false);
  if (!tel?.configured || (compact && !tel.myLine)) return null;

  const onPick = async (line) => {
    try {
      await saveMyLine(line);
      setOpen(false);
      toast.success(`Тепер «Подзвонити» дзвонить з телефону ${line}`);
    } catch (err) {
      toast.error(err?.message || 'Не вдалося зберегти');
    }
  };

  const label = !tel.myLine ? 'Вибрати телефон для «Подзвонити»'
    : compact ? `з ${tel.myLine} · змінити` : `Ваш телефон для «Подзвонити»: ${tel.myLine} · змінити`;
  return (
    <>
      <button type="button" className="dial-line-change" onClick={() => setOpen(true)}>{label}</button>
      {open && <LinePicker current={tel.myLine} onPick={onPick} onClose={() => setOpen(false)} />}
    </>
  );
}
